// File: frontend/src/components/Admin/BankAccountManagement.jsx

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { bankAccountsAPI } from '../../services/api';
import Modal from '../Modal';

const TAIWAN_BANKS = [
  { code: '005', name: '臺灣土地銀行 (Land Bank of Taiwan)' },
  { code: '013', name: '國泰世華銀行 (Cathay United)' },
  { code: '822', name: '中國信託 (CTBC Bank)' },
  { code: '700', name: '中華郵政 (Chunghwa Post)' },
  { code: '012', name: '台北富邦銀行 (Taipei Fubon)' },
  { code: '808', name: '玉山銀行 (E.SUN Bank)' },
  { code: '812', name: '台新銀行 (Taishin Bank)' },
  { code: '004', name: '臺灣銀行 (Bank of Taiwan)' },
  { code: '006', name: '合作金庫 (Taiwan Cooperative Bank)' },
  { code: '007', name: '第一銀行 (First Bank)' },
  { code: '008', name: '華南銀行 (Hua Nan Bank)' },
  { code: '009', name: '彰化銀行 (Chang Hwa Bank)' },
  { code: '017', name: '兆豐國際商銀 (Mega Bank)' },
  { code: 'other', name: '其他銀行 (Other Bank)' },
];

export default function BankAccountManagement() {
  const [accounts, setAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  // Form states for new account
  const [selectedBankPreset, setSelectedBankPreset] = useState('013');
  const [bankName, setBankName] = useState('國泰世華銀行');
  const [bankCode, setBankCode] = useState('013');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('Sule');
  const [branchName, setBranchName] = useState('');
  const [instructions, setInstructions] = useState('轉帳完成後請告知「帳號末5碼」以利對帳，謝謝！');
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const data = await bankAccountsAPI.getAll();
      setAccounts(data);
    } catch (err) {
      console.error("Failed to fetch bank accounts:", err);
    }
  };

  const handleBankPresetChange = (code) => {
    setSelectedBankPreset(code);
    const found = TAIWAN_BANKS.find(b => b.code === code);
    if (found && code !== 'other') {
      const cleanName = found.name.split(' (')[0];
      setBankName(cleanName);
      setBankCode(code);
    } else {
      setBankName('');
      setBankCode('');
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    const payload = {
      bank_name: bankName,
      bank_code: bankCode,
      account_number: accountNumber,
      account_name: accountName,
      branch_name: branchName,
      instructions,
      is_primary: isPrimary
    };

    const addPromise = bankAccountsAPI.create(payload);
    toast.promise(addPromise, {
      loading: 'Adding bank account...',
      success: () => {
        fetchAccounts();
        setIsModalOpen(false);
        setAccountNumber('');
        setBranchName('');
        setIsPrimary(false);
        return <b>Bank account added!</b>;
      },
      error: (err) => <b>{err.response?.data?.error || 'Could not add bank account.'}</b>
    });
  };

  const handleOpenEdit = (acc) => {
    setEditingAccount({ ...acc });
    setIsEditModalOpen(true);
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    if (!editingAccount) return;

    const updatePromise = bankAccountsAPI.update(editingAccount.id, editingAccount);
    toast.promise(updatePromise, {
      loading: 'Updating account...',
      success: () => {
        fetchAccounts();
        setIsEditModalOpen(false);
        return <b>Account updated!</b>;
      },
      error: (err) => <b>{err.response?.data?.error || 'Could not update account.'}</b>
    });
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await bankAccountsAPI.setPrimary(accountId);
      toast.success('Set as primary payment account!');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to set primary account.');
    }
  };

  const handleDelete = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        await bankAccountsAPI.delete(accountId);
        toast.success('Bank account removed.');
        fetchAccounts();
      } catch (err) {
        toast.error('Failed to delete account.');
      }
    }
  };

  // One-click copy formatted message for LINE / SMS to parents
  const handleCopyForLine = (acc) => {
    const text = 
`【轉帳繳費資訊】
銀行名稱：${acc.bank_name}
銀行代碼：${acc.bank_code}
戶名：${acc.account_name}
帳號：${acc.account_number}
${acc.branch_name ? `分行：${acc.branch_name}\n` : ''}※ ${acc.instructions || '轉帳完成後請私訊告知「帳號末5碼」以便對帳，謝謝！'}`;

    navigator.clipboard.writeText(text)
      .then(() => toast.success('📋 Bank info copied! Ready to paste into LINE.'))
      .catch(() => toast.error('Failed to copy to clipboard.'));
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Bank Accounts (轉帳)</h3>
        <button 
          onClick={() => setIsModalOpen(true)} 
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors"
        >
          + Add Account
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Configure Taiwan bank accounts for parent ATM / transfer payments.</p>

      <div className="border rounded p-2 max-h-48 overflow-y-auto space-y-2">
        {accounts.length > 0 ? accounts.map(acc => (
          <div key={acc.id} className="p-2 border rounded hover:bg-gray-50 text-xs">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-1 font-semibold text-gray-900 dark:text-gray-100">
                  <span>{acc.bank_name} ({acc.bank_code})</span>
                  {acc.is_primary && (
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.2 rounded font-bold">
                      ⭐ Primary
                    </span>
                  )}
                </div>
                <div className="font-mono text-gray-800 dark:text-gray-200 font-medium text-sm mt-0.5 tracking-wider">
                  {acc.account_number}
                </div>
                <div className="text-gray-500 dark:text-gray-400 text-[11px]">
                  戶名: <span className="font-medium text-gray-700 dark:text-gray-300">{acc.account_name}</span>
                  {acc.branch_name && ` • 分行: ${acc.branch_name}`}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleCopyForLine(acc)}
                title="Copy formatted bank details for LINE"
                className="bg-green-50 hover:bg-green-100 text-green-700 border border-green-300 px-2 py-1 rounded text-[11px] font-medium flex items-center gap-1"
              >
                📋 LINE
              </button>
            </div>

            <div className="flex justify-end gap-2 mt-2 pt-1 border-t border-gray-100 dark:border-gray-700">
              {!acc.is_primary && (
                <button 
                  onClick={() => handleSetPrimary(acc.id)} 
                  className="text-indigo-600 hover:text-indigo-800 text-[11px]"
                >
                  Set Primary
                </button>
              )}
              <button 
                onClick={() => handleOpenEdit(acc)} 
                className="text-blue-600 hover:text-blue-800 text-[11px]"
              >
                Edit
              </button>
              <button 
                onClick={() => handleDelete(acc.id)} 
                className="text-red-500 hover:text-red-700 text-[11px]"
              >
                Delete
              </button>
            </div>
          </div>
        )) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">No bank accounts added yet. Click "+ Add Account" to set up your account numbers.</p>
        )}
      </div>

      {/* Add Bank Account Modal */}
      <Modal title="Add Bank Account (新增轉帳帳戶)" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <form onSubmit={handleAddAccount} className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Select Bank (選擇銀行)</label>
            <select 
              value={selectedBankPreset} 
              onChange={e => handleBankPresetChange(e.target.value)}
              className="w-full border rounded p-2"
            >
              {TAIWAN_BANKS.map(b => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name (銀行名稱)</label>
              <input 
                type="text" 
                value={bankName} 
                onChange={e => setBankName(e.target.value)} 
                className="w-full border rounded p-2" 
                required 
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Code (銀行代號 3碼)</label>
              <input 
                type="text" 
                value={bankCode} 
                onChange={e => setBankCode(e.target.value)} 
                placeholder="013" 
                className="w-full border rounded p-2 font-mono" 
                required 
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number (銀行帳號)</label>
            <input 
              type="text" 
              value={accountNumber} 
              onChange={e => setAccountNumber(e.target.value)} 
              placeholder="e.g. 056-50-123456-7" 
              className="w-full border rounded p-2 font-mono text-base font-medium" 
              required 
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name (戶名)</label>
              <input 
                type="text" 
                value={accountName} 
                onChange={e => setAccountName(e.target.value)} 
                placeholder="e.g. Sule" 
                className="w-full border rounded p-2" 
                required 
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Branch Name (分行, 選填)</label>
              <input 
                type="text" 
                value={branchName} 
                onChange={e => setBranchName(e.target.value)} 
                placeholder="e.g. 大安分行" 
                className="w-full border rounded p-2" 
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Instructions (轉帳提醒)</label>
            <textarea 
              value={instructions} 
              onChange={e => setInstructions(e.target.value)} 
              className="w-full border rounded p-2 text-xs" 
              rows="2"
            ></textarea>
          </div>

          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="is_primary" 
              checked={isPrimary} 
              onChange={e => setIsPrimary(e.target.checked)} 
              className="mr-2 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" 
            />
            <label htmlFor="is_primary" className="text-xs text-gray-700 dark:text-gray-300 font-medium">Set as primary default account (設為預設收款帳號)</label>
          </div>

          <div className="flex justify-end space-x-2 pt-2 border-t">
            <button type="button" onClick={() => setIsModalOpen(false)} className="bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-xs">Cancel</button>
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-xs font-medium">Save Account</button>
          </div>
        </form>
      </Modal>

      {/* Edit Bank Account Modal */}
      {editingAccount && (
        <Modal title="Edit Bank Account (編輯帳戶)" isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <form onSubmit={handleUpdateAccount} className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name</label>
                <input 
                  type="text" 
                  value={editingAccount.bank_name || ''} 
                  onChange={e => setEditingAccount({ ...editingAccount, bank_name: e.target.value })} 
                  className="w-full border rounded p-2" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Code</label>
                <input 
                  type="text" 
                  value={editingAccount.bank_code || ''} 
                  onChange={e => setEditingAccount({ ...editingAccount, bank_code: e.target.value })} 
                  className="w-full border rounded p-2 font-mono" 
                  required 
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number</label>
              <input 
                type="text" 
                value={editingAccount.account_number || ''} 
                onChange={e => setEditingAccount({ ...editingAccount, account_number: e.target.value })} 
                className="w-full border rounded p-2 font-mono text-base font-medium" 
                required 
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name (戶名)</label>
                <input 
                  type="text" 
                  value={editingAccount.account_name || ''} 
                  onChange={e => setEditingAccount({ ...editingAccount, account_name: e.target.value })} 
                  className="w-full border rounded p-2" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Branch (分行)</label>
                <input 
                  type="text" 
                  value={editingAccount.branch_name || ''} 
                  onChange={e => setEditingAccount({ ...editingAccount, branch_name: e.target.value })} 
                  className="w-full border rounded p-2" 
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Instructions</label>
              <textarea 
                value={editingAccount.instructions || ''} 
                onChange={e => setEditingAccount({ ...editingAccount, instructions: e.target.value })} 
                className="w-full border rounded p-2 text-xs" 
                rows="2"
              ></textarea>
            </div>

            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="edit_is_primary" 
                checked={editingAccount.is_primary || false} 
                onChange={e => setEditingAccount({ ...editingAccount, is_primary: e.target.checked })} 
                className="mr-2 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" 
              />
              <label htmlFor="edit_is_primary" className="text-xs text-gray-700 dark:text-gray-300 font-medium">Set as primary default account</label>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t">
              <button type="button" onClick={() => setIsEditModalOpen(false)} className="bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-xs">Cancel</button>
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-xs font-medium">Save Changes</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
